import type { AppState } from '../../store/store';
import { ApiErrorEnum } from '../../enums/ApiErrorEnum';
import { useAppStore } from '../../store/store';

/**
 * Backend error payload for folder/file password (inside response.data.value).
 */
interface FilePasswordErrorValue {
  errorCode: string;
  folderId: number;
  folderName: string;
  message?: string;
  isLockedOut?: boolean;
  lockoutMinutesRemaining?: number | null;
}

/**
 * Handles file (folder) password-related errors (403 PASSWORD_REQUIRED and 429 LOCKED_OUT).
 * Backend sends: { name: "FOLDER_PASSWORD_REQUIRED", value: { errorCode, folderId, folderName, message, isLockedOut, lockoutMinutesRemaining } }.
 * This function should be called in catch blocks of services/hooks that make API calls
 * that might trigger folder password errors.
 *
 * @param error - The error object from the API call
 * @returns True if the error was handled (PASSWORD_REQUIRED or LOCKED_OUT), false otherwise
 */
export function handleFilePasswordError(error: any): boolean {
  const status = error?.status || error?.response?.status;
  const errorData = error?.response?.data || error?.response;
  if (!errorData || typeof errorData !== 'object') return false;

  // New format: { name: "FOLDER_PASSWORD_REQUIRED", value: { errorCode, folderId, folderName, ... } }
  const errorValue: FilePasswordErrorValue | undefined = errorData.value;
  const errorCode = errorValue?.errorCode ?? errorData.errorCode;

  // Handle PASSWORD_REQUIRED: 403 from backend, or 200 with body { name: "FOLDER_PASSWORD_REQUIRED", value: { ... } }
  const isPasswordRequired =
    errorCode === ApiErrorEnum.PasswordRequired &&
    (status === 403 || errorData?.name === ApiErrorEnum.FolderPasswordRequired);
  if (isPasswordRequired) {
    const payload = errorValue ?? errorData;
    const folderId = payload.folderId ?? 0;
    const folderName = payload.folderName ?? 'Unknown';

    const store = useAppStore.getState() as AppState;
    // Backend rejected the request (e.g. token invalid/expired); remove any stored session for this folder so we don't reuse it
    if (folderId != null && Number.isFinite(folderId)) {
      store.removeFilePasswordSession(folderId);
    }
    let requestedFileId: number | undefined;
    try {
      const data = error.config?.data;
      const body = typeof data === 'string' ? JSON.parse(data) : data;
      if (
        body != null &&
        typeof body === 'object' &&
        body.parent !== undefined &&
        body.parent !== null
      ) {
        const parsed =
          typeof body.parent === 'string' ? parseInt(body.parent, 10) : Number(body.parent);
        if (typeof parsed === 'number' && !isNaN(parsed)) {
          requestedFileId = parsed;
        }
      }
    } catch {
      // Ignore parse errors; requestedFileId stays undefined
    }
    const request = {
      fileId: folderId,
      fileName: folderName,
      originalRequest: error.config,
      ...(requestedFileId !== undefined && { requestedFileId }),
    };
    store.setPendingFilePasswordRequest(request);
    return true;
  }

  // Handle LOCKED_OUT: 429 from backend, or 200 with body { name: "FOLDER_PASSWORD_LOCKOUT", value: { ... } }
  const isLockedOut =
    errorCode === ApiErrorEnum.LockedOut &&
    (status === 429 || errorData?.name === ApiErrorEnum.FolderPasswordLockout);
  if (isLockedOut) {
    const payload = errorValue ?? errorData;
    const folderId = payload.folderId;
    const lockoutMinutesRemaining = payload.lockoutMinutesRemaining ?? 0;
    if (folderId != null) {
      const store = useAppStore.getState() as AppState;
      store.removeFilePasswordSession(folderId);
      store.setFilePasswordLockoutInfo(folderId, lockoutMinutesRemaining);
      return true;
    }
  }

  return false;
}
